<template>
  <button
    type="button"
    :class="[
      paddingClass,
      fontSizeClass,
      leadingClass,
      colorClass,
      shadowClass,
      radiusClass,
      'tw-font-sans tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-transparent tw-rounded-md tw-transition tw-ease-in-out tw-duration-100 tw-relative',
    ]"
    :style="customCircleSize"
    @click="handleClick"
  >
    <div v-show="loading" class="tw-absolute tw-flex tw-items-center">
      <base-spinner :size="spinnerSize" current />
    </div>
    <div :class="[{ 'tw-invisible': loading }, 'tw-inline-flex tw-items-center']">
      <div :class="['tw-h-3.5 tw-w-3.5', iconClass, iconColorClass, fontSizeClass]" v-if="$slots.icon">
        <slot name="icon" />
      </div>
      <slot />
    </div>
  </button>
</template>

<script>
import BaseSpinner from "@/components/base/BaseSpinner";

export default {
  components: {
    BaseSpinner,
  },
  props: {
    type: {
      type: String,
      default: "primary",
      validator: (value) => ["primary", "secondary", "flat", "text"].indexOf(value) !== -1,
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["xs", "sm", "md", "lg", "xl"].indexOf(value) !== -1,
    },
    color: {
      type: String,
      default: "primary",
      validator: (value) => ["primary", "secondary", "red", "green", "gray", "yellow"].indexOf(value) !== -1,
    },
    disabled: Boolean,
    loading: Boolean,
    circle: { type: Boolean, default: false },
    circleRadius: Number,
  },
  computed: {
    paddingClass() {
      if (this.type === "text") return "";

      switch (this.size) {
        case "xs":
          return "tw-px-2.5 tw-py-1.5";
        case "sm":
          return "tw-px-3 tw-py-2";
        case "md":
        case "lg":
          return "tw-px-4 tw-py-2";
        case "xl":
          return "tw-px-6 tw-py-3";
      }
      return "";
    },
    radiusClass() {
      if (this.circle === true) return "tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-mono tw-text-center";
      else return "";
    },
    customCircleSize() {
      if (this.circleRadius > 0) return "height: " + this.circleRadius + "px !important; width: " + this.circleRadius + "px !important;";
      else return "";
    },
    iconClass() {
      return this.circle === true ? this.radiusClass : "tw--ml-1 tw-mr-1.5 tw-leading-3";
    },
    fontSizeClass() {
      switch (this.size) {
        case "xs":
          return "tw-text-xs";
        case "sm":
        case "md":
          return "tw-text-sm";
        case "lg":
        case "xl":
          return "tw-text-base";
      }
      return "";
    },
    leadingClass() {
      switch (this.size) {
        case "sm":
          return "tw-leading-4";
      }
      return "";
    },
    colorClass() {
      if (this.disabled) {
        switch (this.type) {
          case "primary":
            return "tw-text-gray-600 tw-bg-gray-200 tw-cursor-not-allowed";
          case "secondary":
            return "tw-text-gray-500 tw-bg-gray-100 tw-cursor-not-allowed";
          case "flat":
          case "text":
            return "tw-text-gray-500 tw-cursor-not-allowed";
        }
      } else {
        switch (this.type) {
          case "primary":
            return `tw-text-white tw-bg-${this.color}-500 hover:tw-bg-${this.color}-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-${
              this.color
            }-500`;
          case "secondary":
            return `tw-text-${this.color}-600 tw-bg-${this.color}-100 hover:tw-bg-${this.color}-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-${
              this.color
            }-500`;
          case "flat":
            return `tw-text-${this.color}-500 tw-bg-transparent hover:tw-bg-${this.color}-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-${
              this.color
            }-500`;
          case "text":
            return `tw-text-${this.color}-500 tw-bg-transparent hover:tw-underline border-0 focus:tw-outline-none focus:tw-text-${this.color}-700`;
        }
      }
      return "";
    },
    shadowClass() {
      return this.type === "primary" ? "tw-shadow" : "tw-shadow-none";
    },
    spinnerSize() {
      switch (this.size) {
        case "xs":
        case "sm":
          return 16;
        case "md":
          return 24;
        case "lg":
        case "xl":
          return 28;
      }

      return 24;
    },
    iconColorClass() {
      if (this.disabled) {
        switch (this.type) {
          case "primary":
            return "tw-text-gray-600";
          case "secondary":
            return "tw-text-gray-500";
          case "flat":
            return "tw-text-gray-500";
        }
      } else {
        switch (this.type) {
          case "primary":
            return `tw-text-${this.color}-100`;
          case "secondary":
            return `tw-text-${this.color}-300`;
          case "flat":
            return `tw-text-${this.color}-500`;
        }
      }

      return "";
    },
  },
  methods: {
    handleClick() {
      if (!this.disabled && !this.loading) {
        this.$emit("click");
      }
    },
  },
};
</script>
